import { BusinessStatusBadge } from "@nwga/features/businesses/ui";
import { isExpired, type Job, useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { EmptyState } from "@nwga/ui/components/empty-state";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { StatTile } from "@nwga/ui/components/stat-tile";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BriefcaseIcon, ClockIcon, FileTextIcon, PlusIcon } from "lucide-react";
import { PageHeader } from "../../../components/page-header.tsx";

/**
 * A business's home inside the workspace.
 *
 * It answers one question — is there anything for me to do — and then gets out
 * of the way. Everything on it is either a count that implies an action or a
 * listing in a state that needs one; there is no "welcome back" panel, because
 * the second visit is the one that matters and by then it is furniture.
 *
 * The listings query is the same one the listings page reads, so opening this
 * first warms that page rather than costing an extra request.
 */
export const Route = createFileRoute("/dashboard/$businessSlug/")({
	component: BusinessOverviewPage,
	loader: async ({ context, params }) => {
		const business = await context.queryClient.ensureQueryData(
			context.trpc.businesses.bySlug.queryOptions({
				slug: params.businessSlug,
			}),
		);

		return context.queryClient.ensureQueryData(
			context.trpc.jobs.forBusiness.queryOptions({
				businessId: business.id,
				limit: 100,
			}),
		);
	},
});

/** How long before a listing closes that its owner should be told. */
const CLOSING_SOON_DAYS = 7;

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });

function daysUntil(value: Date): number {
	return Math.ceil((value.getTime() - Date.now()) / 86_400_000);
}

function BusinessOverviewPage() {
	const { businessSlug } = Route.useParams();
	const trpc = useTRPC();

	const business = useQuery(
		trpc.businesses.bySlug.queryOptions({ slug: businessSlug }),
	);
	const listings = useQuery({
		...trpc.jobs.forBusiness.queryOptions({
			businessId: business.data?.id ?? "",
			limit: 100,
		}),
		enabled: business.isSuccess,
	});

	if (!business.isSuccess) return <Skeleton className="h-72 w-full" />;

	const jobs = listings.data?.items ?? [];
	const live = jobs.filter(
		(job) => job.status === "published" && !isExpired(job),
	);
	const drafts = jobs.filter((job) => job.status === "draft");
	const closingSoon = live.filter((job) => {
		if (job.expiresAt === null) return false;
		return daysUntil(job.expiresAt) <= CLOSING_SOON_DAYS;
	});

	const approved = business.data.status === "active";

	return (
		<div className="space-y-8">
			<PageHeader
				title={business.data.name}
				description={
					approved
						? "Your listings and who can manage them."
						: "You can write listings now; publishing unlocks once the Authority approves this business."
				}
				meta={
					business.data.status !== "active" && (
						<BusinessStatusBadge status={business.data.status} />
					)
				}
				actions={
					<Button asChild>
						<Link
							to="/dashboard/$businessSlug/jobs/new"
							params={{ businessSlug }}
						>
							<PlusIcon className="size-4" aria-hidden="true" />
							Post a job
						</Link>
					</Button>
				}
			/>

			{listings.isPending ? (
				<Skeleton className="h-24 w-full" />
			) : (
				<div className="grid gap-3 sm:grid-cols-3">
					<StatTile
						icon={BriefcaseIcon}
						label="Live listings"
						value={live.length}
						hint={
							live.length === 0
								? "Nothing on the board right now"
								: "Visible to job seekers"
						}
					/>
					<StatTile
						icon={FileTextIcon}
						label="Drafts"
						value={drafts.length}
						hint={
							drafts.length === 0
								? "None waiting"
								: "Private until you publish them"
						}
						tone={drafts.length > 0 && approved ? "attention" : "default"}
					/>
					<StatTile
						icon={ClockIcon}
						label="Closing soon"
						value={closingSoon.length}
						hint={`Within ${CLOSING_SOON_DAYS} days`}
						tone={closingSoon.length > 0 ? "attention" : "default"}
					/>
				</div>
			)}

			<section className="space-y-3">
				<div className="flex flex-wrap items-baseline justify-between gap-3">
					<h2 className="font-medium text-lg">Needs your attention</h2>
					<Button asChild variant="ghost" size="sm">
						<Link to="/dashboard/$businessSlug/jobs" params={{ businessSlug }}>
							All listings
						</Link>
					</Button>
				</div>

				<AttentionList
					businessSlug={businessSlug}
					pending={listings.isPending}
					drafts={drafts}
					closingSoon={closingSoon}
					canPublish={approved}
					hasListings={jobs.length > 0}
				/>
			</section>
		</div>
	);
}

/**
 * The listings in a state their owner has to decide about.
 *
 * Drafts and imminent closures, and nothing else — a "recent listings" list
 * here would just be the listings page rendered twice, and the thing that
 * silently takes a role off the board is an expiry nobody noticed.
 */
function AttentionList({
	businessSlug,
	pending,
	drafts,
	closingSoon,
	canPublish,
	hasListings,
}: {
	readonly businessSlug: string;
	readonly pending: boolean;
	readonly drafts: readonly Job[];
	readonly closingSoon: readonly Job[];
	readonly canPublish: boolean;
	readonly hasListings: boolean;
}) {
	if (pending) return <Skeleton className="h-32 w-full" />;

	if (!hasListings) {
		return (
			<EmptyState
				icon={BriefcaseIcon}
				title="No listings yet"
				description={
					canPublish
						? "Post your first opening and it appears on the board straight away."
						: "Write your first opening now — it can go live the moment you're approved."
				}
				action={
					<Button asChild>
						<Link
							to="/dashboard/$businessSlug/jobs/new"
							params={{ businessSlug }}
						>
							Post a job
						</Link>
					</Button>
				}
			/>
		);
	}

	if (drafts.length === 0 && closingSoon.length === 0) {
		return (
			<p className="rounded-lg border border-dashed p-6 text-center text-muted-foreground text-sm">
				Nothing waiting on you. Every listing is live and none closes in the
				next {CLOSING_SOON_DAYS} days.
			</p>
		);
	}

	return (
		<ul className="space-y-2">
			{drafts.map((job) => (
				<AttentionRow
					key={job.id}
					businessSlug={businessSlug}
					job={job}
					note={
						canPublish
							? "Draft — not on the board yet"
							: "Draft — publishes once approved"
					}
				/>
			))}

			{closingSoon.map((job) => (
				<AttentionRow
					key={job.id}
					businessSlug={businessSlug}
					job={job}
					note={
						job.expiresAt === null
							? "Closing soon"
							: `Closes ${DATE.format(job.expiresAt)} — extend it if the role is still open`
					}
				/>
			))}
		</ul>
	);
}

function AttentionRow({
	businessSlug,
	job,
	note,
}: {
	readonly businessSlug: string;
	readonly job: Job;
	readonly note: string;
}) {
	return (
		<li>
			<Link
				to="/dashboard/$businessSlug/jobs/$jobId"
				params={{ businessSlug, jobId: job.id }}
				className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-brand/60 hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
			>
				<span className="min-w-0">
					<span className="block truncate font-medium">{job.title}</span>
					<span className="mt-0.5 block text-muted-foreground text-sm">
						{note}
					</span>
				</span>
				<span className="text-muted-foreground text-sm">Open</span>
			</Link>
		</li>
	);
}
